import { createFileRoute } from "@tanstack/react-router"
import { useState, useMemo } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createCollectionSchema } from "@/lib/seo"
import { tools, ALL_CATEGORIES, getCategoryLabel } from "@/data/tools/tools-registry"
import type { ToolCategory } from "@/data/tools/tools-registry"
import ToolCard from "@/components/tools/ToolCard"
import Heading from "@/components/layout/Heading"
import CTA from "@/components/LandingPage/CTA"
import { useTranslation } from "@/lib/i18n"

const liveTools = tools.filter((t) => t.status === "live")

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Free Instagram Tools — Calculators, Generators & More | Zapify",
        description:
          "30+ free Instagram tools: engagement calculator, money calculator, caption generator, hashtag finder, DM ROI calculator & more. No signup required.",
        url: "https://zapify.pro/tools",
      }),
      {
        name: "keywords",
        content:
          "free instagram tools, instagram calculator, engagement rate calculator, instagram money calculator, caption generator, hashtag generator",
      },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools" }],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        ],
      }),
      createCollectionSchema({
        name: "Free Instagram Tools by Zapify",
        description:
          "Free Instagram tools for creators: calculators, generators, formatters, and more.",
        url: "https://zapify.pro/tools",
        items: liveTools.map((t) => ({
          name: t.name,
          url: `https://zapify.pro/tools/${t.slug}`,
        })),
      }),
    ],
  }),
  component: ToolsDirectoryPage,
})

function ToolsDirectoryPage() {
  const { translations: t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState<ToolCategory | "all">("all")
  const [search, setSearch] = useState("")

  const filteredTools = useMemo(() => {
    let result = tools
    if (activeCategory !== "all") {
      result = result.filter((t) => t.category === activeCategory)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.tags.some((tag) => tag.includes(q))
      )
    }
    // live tools first, then coming-soon
    return result.sort((a, b) => {
      if (a.status === "live" && b.status !== "live") return -1
      if (a.status !== "live" && b.status === "live") return 1
      return 0
    })
  }, [activeCategory, search])

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Hero */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Heading
            title={<>{t.toolsPage.heading} <span className="gradient-text">{t.toolsPage.headingHighlight}</span></>}
            description={t.toolsPage.description}
            className="mb-6 sm:mb-10"
            showLine={false}
          />

          {/* Search */}
          <div className="max-w-md mx-auto mb-6">
            <input
              type="text"
              placeholder={t.toolsPage.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-button/40 focus:border-brand-button/40 transition-all"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex items-center justify-center gap-2 flex-wrap mb-8 sm:mb-10">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeCategory === "all"
                  ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm"
                  : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700"
              }`}
            >
              {t.toolsPage.all} ({tools.length})
            </button>
            {ALL_CATEGORIES.map((cat) => {
              const count = tools.filter((t) => t.category === cat).length
              if (count === 0) return null
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    activeCategory === cat
                      ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm"
                      : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                  }`}
                >
                  {getCategoryLabel(cat)} ({count})
                </button>
              )
            })}
          </div>

          {/* Tool Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {filteredTools.map((tool) => (
              <ToolCard key={tool.slug} tool={tool} />
            ))}
          </div>

          {filteredTools.length === 0 && (
            <div className="text-center py-16">
              <p className="text-neutral-500 dark:text-neutral-400 font-medium">
                {t.toolsPage.noToolsFound}
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <CTA />
      </div>
    </div>
  )
}
